<template>
  <div class="min-h-screen bg-black text-white">
    <section class="relative h-screen flex items-center justify-center">
      <!-- Background Image -->
      <div
          class="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style="background-image: url('/images/byd-interior.jpg')"
      ></div>

      <!-- Darkening Overlay -->
      <div class="absolute inset-0 bg-black bg-opacity-60"></div>

      <!-- Content -->
      <div class="relative z-10 text-center max-w-4xl px-4">
        <h1 class="text-5xl font-bold mb-6 animate-fade-in">
          Transform Your Business with Efficient Electric Solutions
        </h1>
        <p class="text-xl text-green-400 mb-8 animate-slide-up">
          Sustainable. Efficient. Future-Ready.
        </p>
        <button
            @click="startJourney"
            class="bg-green-500 hover:bg-green-600 text-white px-8 py-3 rounded-full text-lg transition-all transform hover:scale-105"
        >
          Start Your Journey
        </button>
        <div class="mt-12 text-center">
          <p class="text-sm text-gray-400 mb-4">Official Partners</p>
          <div class="flex items-center justify-center space-x-8">
            <img src="/images/logo/driveev.png" alt="DriveEV" class="h-12 opacity-80 hover:opacity-100 transition-opacity">
            <img src="/images/logo/plugdrive.png" alt="PlugDrive" class="h-12 opacity-80 hover:opacity-100 transition-opacity">
          </div>
        </div>
      </div>
    </section>

    <!-- Slide 1: Benefits Overview -->
    <transition name="fade">
      <div v-if="currentSlide === 1" class="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-20">
        <div class="bg-gray-900 p-8 rounded-lg max-w-4xl w-full mx-4">
          <h2 class="text-3xl font-bold mb-8 text-center">Why Switch to Electric Vehicles?</h2>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div class="bg-gray-800 p-6 rounded-lg">
              <h3 class="text-xl font-bold mb-4 text-green-400">Environmental Impact</h3>
              <ul class="space-y-3">
                <li class="flex items-center">
                  <span class="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                  Zero direct emissions
                </li>
                <li class="flex items-center">
                  <span class="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                  Reduced carbon footprint
                </li>
                <li class="flex items-center">
                  <span class="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                  Lower noise pollution
                </li>
                <li class="flex items-center">
                  <span class="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                  Support sustainable transportation
                </li>
              </ul>
            </div>
            <div class="bg-gray-800 p-6 rounded-lg">
              <h3 class="text-xl font-bold mb-4 text-green-400">Economic Benefits</h3>
              <ul class="space-y-3">
                <li class="flex items-center">
                  <span class="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                  Lower operating costs
                </li>
                <li class="flex items-center">
                  <span class="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                  Reduced maintenance expenses
                </li>
                <li class="flex items-center">
                  <span class="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                  Government incentives
                </li>
                <li class="flex items-center">
                  <span class="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                  Higher resale value
                </li>
              </ul>
            </div>
          </div>
          <div class="mt-8 flex justify-center">
            <button
                @click="nextSlide"
                class="bg-green-500 hover:bg-green-600 px-8 py-3 rounded-lg transition-all transform hover:scale-105 flex items-center"
            >
              Next
              <svg class="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Slide 2: Solar Charging Solution -->
    <transition name="fade">
      <div v-if="currentSlide === 2" class="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-20">
        <div class="bg-gray-900 p-8 rounded-lg max-w-5xl w-full mx-4">
          <h2 class="text-3xl font-bold mb-8 text-center">Solar Charging Solution</h2>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
            <!-- Solar Infrastructure -->
            <div class="bg-gray-800 p-6 rounded-lg">
              <h3 class="text-xl font-bold mb-4 text-green-400">Solar Charging Infrastructure</h3>
              <div class="space-y-4">
                <div class="flex items-start space-x-3">
                  <svg class="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                  <div>
                    <h4 class="font-semibold">Solar Canopy</h4>
                    <p class="text-sm text-gray-300">Custom-designed solar canopy that provides both charging power and shade for your vehicles</p>
                  </div>
                </div>

                <div class="flex items-start space-x-3">
                  <svg class="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <div>
                    <h4 class="font-semibold">Fast Charging</h4>
                    <p class="text-sm text-gray-300">Advanced charging stations with up to 150kW DC fast charging capability</p>
                  </div>
                </div>

                <div class="flex items-start space-x-3">
                  <svg class="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <h4 class="font-semibold">Smart Management</h4>
                    <p class="text-sm text-gray-300">Intelligent load balancing and energy management system</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Cost Benefits -->
            <div class="bg-gray-800 p-6 rounded-lg">
              <h3 class="text-xl font-bold mb-4 text-green-400">Financial Benefits</h3>
              <div class="space-y-4">
                <div class="flex items-start space-x-3">
                  <svg class="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <h4 class="font-semibold">Energy Cost Reduction</h4>
                    <p class="text-sm text-gray-300">Up to 70% reduction in charging costs compared to grid electricity</p>
                  </div>
                </div>

                <div class="flex items-start space-x-3">
                  <svg class="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <div>
                    <h4 class="font-semibold">Government Incentives</h4>
                    <p class="text-sm text-gray-300">Eligible for solar installation tax benefits and green energy credits</p>
                  </div>
                </div>

                <div class="flex items-start space-x-3">
                  <svg class="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                  <div>
                    <h4 class="font-semibold">ROI Timeline</h4>
                    <p class="text-sm text-gray-300">Average payback period of 4-5 years with ongoing savings</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="mt-8 bg-gray-800 p-6 rounded-lg">
            <h3 class="text-xl font-bold mb-4 text-center">Estimated Annual Savings with Solar Charging</h3>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
              <div class="p-4">
                <p class="text-green-400 mb-2">5 Vehicle Fleet</p>
                <p class="text-2xl font-bold">GHS 45,000+</p>
              </div>
              <div class="p-4">
                <p class="text-green-400 mb-2">10 Vehicle Fleet</p>
                <p class="text-2xl font-bold">GHS 95,000+</p>
              </div>
              <div class="p-4">
                <p class="text-green-400 mb-2">20+ Vehicle Fleet</p>
                <p class="text-2xl font-bold">GHS 200,000+</p>
              </div>
            </div>
          </div>

          <div class="mt-8 flex justify-center">
            <button
                @click="nextSlide"
                class="bg-green-500 hover:bg-green-600 px-8 py-3 rounded-lg transition-all transform hover:scale-105 flex items-center"
            >
              Calculate Your Savings
              <svg class="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Fleet Selection Modal (now Slide 3) -->
    <transition name="fade">
      <div v-if="currentSlide === 3" class="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-20">
        <div class="bg-gray-900 p-8 rounded-lg max-w-md w-full mx-4 transform transition-all">
          <h2 class="text-2xl font-bold mb-6 text-center">What best describes you?</h2>
          <div class="space-y-4">
            <button
                @click="setFleetType('business')"
                class="w-full bg-green-500 hover:bg-green-600 py-3 rounded-lg transition-all transform hover:scale-105"
            >
              I manage a business fleet
            </button>
            <button
                @click="setFleetType('individual')"
                class="w-full bg-gray-700 hover:bg-gray-600 py-3 rounded-lg transition-all transform hover:scale-105"
            >
              I'm an individual
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Fleet Selection Modal -->
    <transition name="fade">
      <div v-if="showFleetQuestion" class="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-20">
        <div class="bg-gray-900 p-8 rounded-lg max-w-md w-full mx-4 transform transition-all"
             :class="{ 'translate-y-0 opacity-100': showFleetQuestion, 'translate-y-4 opacity-0': !showFleetQuestion }">
          <h2 class="text-2xl font-bold mb-6 text-center">What best describes you?</h2>
          <div class="space-y-4">
            <button
                @click="setFleetType('business')"
                class="w-full bg-green-500 hover:bg-green-600 py-3 rounded-lg transition-all transform hover:scale-105"
            >
              I manage a business fleet
            </button>
            <button
                @click="setFleetType('individual')"
                class="w-full bg-gray-700 hover:bg-gray-600 py-3 rounded-lg transition-all transform hover:scale-105"
            >
              I'm an individual
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Enhanced Fleet Calculator Modal -->
    <transition name="fade">
      <div v-if="showCalculator" class="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-20">
        <div class="bg-gray-900 p-8 rounded-lg max-w-5xl w-full mx-4 transform transition-all">
          <h2 class="text-2xl font-bold mb-6">Fleet Cost Calculator</h2>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
            <!-- Current Fleet Section -->
            <div>
              <h3 class="text-xl mb-4">Current Fleet</h3>
              <div class="space-y-4">
                <div>
                  <label class="block mb-2">Vehicle Model</label>
                  <select v-model="calculator.currentFleet.model" class="w-full bg-gray-800 p-2 rounded">
                    <option value="toyota">Toyota Corolla</option>
                    <option value="honda">Honda Civic</option>
                    <option value="hyundai">Hyundai Elantra</option>
                  </select>
                </div>
                <div>
                  <label class="block mb-2">Number of Vehicles</label>
                  <input
                      v-model="calculator.currentFleet.vehicles"
                      type="number"
                      class="w-full bg-gray-800 p-2 rounded"
                  />
                </div>
                <div>
                  <label class="block mb-2">Annual Mileage per Vehicle</label>
                  <input
                      v-model="calculator.currentFleet.mileage"
                      type="number"
                      class="w-full bg-gray-800 p-2 rounded"
                  />
                </div>
                <div>
                  <label class="block mb-2">Current Fuel Cost (GHS/liter)</label>
                  <input
                      v-model="calculator.currentFleet.fuelCost"
                      type="number"
                      class="w-full bg-gray-800 p-2 rounded"
                  />
                </div>
              </div>
            </div>

            <!-- Proposed EV Fleet Section -->
            <div>
              <h3 class="text-xl mb-4">Proposed EV Fleet</h3>
              <div class="space-y-4">
                <div>
                  <label class="block mb-2">Select EV Model</label>
                  <select v-model="calculator.evFleet.model" class="w-full bg-gray-800 p-2 rounded">
                    <option v-for="vehicle in vehicles" :key="vehicle.id" :value="vehicle.id">
                      {{ vehicle.name }} - GHS {{ vehicle.price.toLocaleString() }}
                    </option>
                  </select>
                </div>
                <div class="bg-gray-800 p-4 rounded">
                  <h4 class="font-bold mb-2">Selected EV Specifications</h4>
                  <ul v-if="selectedEV" class="space-y-1">
                    <li v-for="spec in selectedEV.specs" :key="spec">{{ spec }}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <!-- Savings Summary -->
          <div class="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div class="bg-gray-800 p-4 rounded transform transition-all hover:scale-105">
              <p class="text-green-400">Annual Fuel Savings</p>
              <p class="text-2xl font-bold">GHS {{ calculateFuelSavings().toLocaleString() }}</p>
            </div>
            <div class="bg-gray-800 p-4 rounded transform transition-all hover:scale-105">
              <p class="text-green-400">Annual Maintenance Savings</p>
              <p class="text-2xl font-bold">GHS {{ calculateMaintenanceSavings().toLocaleString() }}</p>
            </div>
            <div class="bg-gray-800 p-4 rounded transform transition-all hover:scale-105">
              <p class="text-green-400">Total Annual Savings</p>
              <p class="text-2xl font-bold">GHS {{ calculateTotalSavings().toLocaleString() }}</p>
            </div>
          </div>

          <div class="mt-8 flex justify-center space-x-4">
            <button
                @click="showVehicleModal = true; showCalculator = false"
                class="bg-green-500 hover:bg-green-600 px-6 py-2 rounded-lg transition-all transform hover:scale-105"
            >
              View Available Vehicles
            </button>
            <button
                @click="showCalculator = false"
                class="bg-gray-700 hover:bg-gray-600 px-6 py-2 rounded-lg transition-all transform hover:scale-105"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Vehicle Showcase Modal -->
    <transition name="fade">
      <div v-if="showVehicleModal" class="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-20">
        <div class="bg-gray-900 p-8 rounded-lg max-w-6xl w-full mx-4 h-3/4 overflow-y-auto">
          <h2 class="text-3xl font-bold mb-8 text-center">Available Electric Vehicles</h2>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div
                v-for="vehicle in vehicles"
                :key="vehicle.id"
                class="bg-gray-800 rounded-lg overflow-hidden transform transition-all hover:scale-105"
            >
              <img :src="vehicle.image" :alt="vehicle.name" class="w-full h-48 object-cover">
              <div class="p-6">
                <h3 class="text-xl font-bold mb-2">{{ vehicle.name }}</h3>
                <p class="text-green-400 mb-4">Starting from GHS {{ vehicle.price.toLocaleString() }}</p>
                <ul class="space-y-2 mb-6">
                  <li v-for="spec in vehicle.specs" :key="spec">{{ spec }}</li>
                </ul>
                <button
                    @click="selectVehicle(vehicle)"
                    class="w-full bg-green-500 hover:bg-green-600 py-2 rounded transition-all transform hover:scale-105"
                >
                  Calculate Savings
                </button>
              </div>
            </div>
          </div>
          <div class="mt-8 text-center">
            <button
                @click="showVehicleModal = false"
                class="bg-gray-700 hover:bg-gray-600 px-6 py-2 rounded-lg transition-all transform hover:scale-105"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
const showFleetQuestion = ref(false)
const currentSlide = ref(0)
const showCalculator = ref(false)
const showVehicleModal = ref(false)

const calculator = ref({
  currentFleet: {
    model: 'toyota',
    vehicles: 10,
    mileage: 20000,
    fuelCost: 14.40
  },
  evFleet: {
    model: 1
  }
})

const vehicles = ref([
  {
    id: 1,
    name: 'XPeng P7',
    price: 355000,
    image: '/images/xpeng.jpg',
    specs: [
      '480 km Range',
      '0-100 km/h in 4.5s',
      '196 kW Motor',
      'Fast Charging Support',
      'Energy Consumption: 18.5 kWh/100km'
    ],
    consumption: 18.5
  },
  {
    id: 2,
    name: 'NETA X',
    price: 280000,
    image: '/images/neta-x400.jpg',
    specs: [
      '400 km Range',
      '0-100 km/h in 5.9s',
      '150 kW Motor',
      'Level 2 Autonomous Driving',
      'Energy Consumption: 16.8 kWh/100km'
    ],
    consumption: 16.8
  },
  {
    id: 3,
    name: 'BYD Sealion',
    price: 320000,
    image: '/images/byd-sealion.jpg',
    specs: [
      '450 km Range',
      '0-100 km/h in 5.2s',
      '180 kW Motor',
      'Premium Interior',
      'Energy Consumption: 17.2 kWh/100km'
    ],
    consumption: 17.2
  }
])

const fuelEfficiency = {
  toyota: 12,
  honda: 13,
  hyundai: 11.5
}

const maintenanceCosts = {
  toyota: 4500,
  honda: 4200,
  hyundai: 4000
}

// Computed Properties
const selectedEV = computed(() =>
    vehicles.value.find(v => v.id === calculator.value.evFleet.model)
)

// Methods
const startJourney = () => {
  currentSlide.value = 1
}

const nextSlide = () => {
  currentSlide.value++
}

const setFleetType = (type) => {
  if (type === 'business') {
    showCalculator.value = true
  } else {
    showVehicleModal.value = true
  }
  currentSlide.value = 0
}

const selectVehicle = (vehicle) => {
  calculator.value.evFleet.model = vehicle.id
  showVehicleModal.value = false
  showCalculator.value = true
}

const calculateFuelSavings = () => {
  const currentFuelCost = calculator.value.currentFleet.vehicles *
      calculator.value.currentFleet.mileage *
      (calculator.value.currentFleet.fuelCost / fuelEfficiency[calculator.value.currentFleet.model])

  const evConsumption = selectedEV.value ? selectedEV.value.consumption : 17.5
  const evEnergyCost = calculator.value.currentFleet.vehicles *
      calculator.value.currentFleet.mileage *
      (evConsumption / 100) * 5

  return currentFuelCost - evEnergyCost
}

const calculateMaintenanceSavings = () => {
  const currentMaintenance = calculator.value.currentFleet.vehicles *
      maintenanceCosts[calculator.value.currentFleet.model]
  const evMaintenance = calculator.value.currentFleet.vehicles * 1500
  return currentMaintenance - evMaintenance
}

const calculateTotalSavings = () => {
  return calculateFuelSavings() + calculateMaintenanceSavings()
}

</script>

<style scoped>
/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Animations */
.animate-fade-in {
  animation: fadeIn 1s ease-out;
}

.animate-slide-up {
  animation: slideUp 0.8s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Particle JS Container */
#particles-js {
  position: absolute;
  width: 100%;
  height: 100%;
  background-color: #000000;
  background-image: linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7));
  background-repeat: no-repeat;
  background-size: cover;
  background-position: 50% 50%;
}

/* Custom Scrollbar for modals */
.overflow-y-auto {
  scrollbar-width: thin;
  scrollbar-color: #10B981 #1F2937;
}

.overflow-y-auto::-webkit-scrollbar {
  width: 8px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #1F2937;
  border-radius: 4px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: #10B981;
  border-radius: 4px;
}

/* Input and Select Focus States */
input:focus,
select:focus {
  outline: 2px solid #10B981;
  outline-offset: -2px;
}

/* Hover Effects */
.hover-scale {
  transition: transform 0.2s ease;
}

.hover-scale:hover {
  transform: scale(1.05);
}

/* Card Animations */
.bg-gray-800 {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.bg-gray-800:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 20px rgba(16, 185, 129, 0.2);
}

/* Modal Animations */
.modal-enter-active,
.modal-leave-active {
  transition: all 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}
</style>
